import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { moduleService, userService } from '../../services'
import { useAuth } from '../../auth/auth.context'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { Badge } from '../../components/ui/badge'
import { roleToText } from '../../helpers/enum-to-text'

export default function ModuleAssignmentPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<string[]>([])

  const isWeiterbildung = location.pathname.startsWith('/weiterbildung')
  const basePath = isWeiterbildung ? '/weiterbildung' : '/modules'
  const typeLabel = isWeiterbildung
    ? t('common.typeWeiterbildung')
    : t('common.typeOnboarding')
  const typeI18n = {
    type: typeLabel,
    typeArticle: t('common.articleDefFeminine'),
  }

  const {
    data: module,
    isLoading: moduleLoading,
    error: moduleError,
  } = useQuery({
    queryKey: ['module', id],
    queryFn: () => moduleService.getModuleById(id!),
    enabled: !!id,
  })

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['company-users', user?.companyId],
    queryFn: () => userService.getUsers({ companyId: user?.companyId }),
    enabled: !!user?.companyId,
  })

  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['module-assignments', id],
    queryFn: () => moduleService.getModuleAssignments(id!),
    enabled: !!id,
  })

  useEffect(() => {
    if (!assignments) return
    const assignedIds = assignments
      .map(a => a.userId || a.user?.id)
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
    const uniqueAssignedIds = Array.from(new Set(assignedIds))

    setSelectedUsers(uniqueAssignedIds)
    setInitialAssignedUsers(uniqueAssignedIds)
  }, [assignments])

  const hasChanges = useMemo(() => {
    const a = new Set(initialAssignedUsers)
    const b = new Set(selectedUsers)
    if (a.size !== b.size) return true
    for (const id of a) {
      if (!b.has(id)) return true
    }
    return false
  }, [initialAssignedUsers, selectedUsers])

  const saveAssignmentsMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      if (!id) return

      const prev = new Set(initialAssignedUsers)
      const next = new Set(userIds)

      const toAssign: string[] = []
      const toUnassign: string[] = []

      for (const userId of next) {
        if (!prev.has(userId)) toAssign.push(userId)
      }
      for (const userId of prev) {
        if (!next.has(userId)) toUnassign.push(userId)
      }

      if (toAssign.length > 0) {
        await moduleService.assignModule(id, { userIds: toAssign })
      }

      if (toUnassign.length > 0) {
        await Promise.all(toUnassign.map(userId => moduleService.unassignModule(id, userId)))
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['module', id] })
      await queryClient.invalidateQueries({ queryKey: ['module-assignments', id] })
      await queryClient.invalidateQueries({ queryKey: ['company-users'] })
      navigate(basePath)
    },
  })

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    )
  }

  const handleSelectAll = () => {
    if (selectedUsers.length === users?.users?.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(users?.users?.map(u => u.id).filter((id): id is string => Boolean(id)) || [])
    }
  }

  const handleSave = () => {
    if (!id) return
    if (!hasChanges) {
      navigate(basePath)
      return
    }
    saveAssignmentsMutation.mutate(selectedUsers)
  }

  const allSelected = selectedUsers.length === users?.users?.length

  return (
    <PageLayout
      headerProps={{
        title: module?.title || t('modules.assignTitle', typeI18n),
        subtitle: module
          ? t('modules.assignSubtitle', { ...typeI18n, title: module.title })
          : t('modules.assignTitle', typeI18n),
        variant: 'dashboard',
      }}
      isLoading={moduleLoading || usersLoading || assignmentsLoading}
      error={
        moduleError ||
        (!module && !moduleLoading ? new Error(t('errors.loadModule', typeI18n)) : undefined)
      }
      loadingMessage={t('modules.loadData')}
      errorMessage={t('errors.loadData')}
    >
      {!module ? null : (
        <Container>
          <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8'>
            <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('modules.moduleInfo', typeI18n)}</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <dt className='text-sm font-medium text-gray-500'>{t('common.title')}</dt>
                <dd className='mt-1 text-sm text-gray-900'>{module.title}</dd>
              </div>
              <div className='md:col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>{t('common.description')}</dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {module.description || t('modules.noDescription')}
                </dd>
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6'>
            <div className='flex items-center justify-between mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>{t('modules.selectEmployees')}</h2>
              <button
                onClick={handleSelectAll}
                className='text-blue-600 hover:text-blue-700 font-medium text-sm'
              >
                {allSelected ? t('modules.deselectAll') : t('modules.selectAll')}
              </button>
            </div>

            {users?.users && users.users.length > 0 ? (
              <div className='space-y-3'>
                {users.users.map(user => (
                  <label
                    key={user.id}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors duration-200 ${
                      user.id && selectedUsers.includes(user.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type='checkbox'
                      checked={user.id ? selectedUsers.includes(user.id) : false}
                      onChange={() => user.id && handleUserToggle(user.id)}
                      className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                    />
                    <div className='ml-3 flex-1'>
                      <div className='flex items-center justify-between'>
                        <div>
                          <p className='text-sm font-medium text-gray-900'>
                            {user.firstName} {user.lastName}
                          </p>
                          <p className='text-sm text-gray-500'>{user.email}</p>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <Badge
                            title={roleToText(user.role)}
                            variant={
                              user.role === 'ADMIN'
                                ? 'warning'
                                : user.role === 'SUPERADMIN'
                                  ? 'error'
                                  : 'default'
                            }
                          />
                          <Badge
                            title={user.isActive ? t('common.active') : t('common.inactive')}
                            variant={user.isActive ? 'success' : 'error'}
                          />
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className='text-center py-8'>
                <p className='text-gray-500'>{t('modules.noEmployeesFoundDot')}</p>
              </div>
            )}

            <div className='flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200'>
              <button
                onClick={() => navigate(basePath)}
                className='px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saveAssignmentsMutation.isPending || !hasChanges}
                className='bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2'
              >
                {saveAssignmentsMutation.isPending && (
                  <svg
                    className='w-4 h-4 animate-spin'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                    />
                  </svg>
                )}
                <span>
                  {saveAssignmentsMutation.isPending
                    ? t('modules.savingAssignments')
                    : t('modules.saveAssignmentsCount', { count: selectedUsers.length })}
                </span>
              </button>
            </div>
          </div>
        </Container>
      )}
    </PageLayout>
  )
}
